/**
 * 作战台顶部环节条（Optical 重构）：
 * - 左侧巨型环节序号（金属渐变）+ 环节名（hud-stage-name）；
 * - 中段玻璃数据晶片，右侧热键与连接状态；
 * - 所有信息压缩为单行光学条，不再是一张嵌套卡片。
 */
import type { MatchPhase, Participant, Side, StageDefinition } from '@zongheng/core-schema';
import { sideLabel } from '@zongheng/core-schema';
import { Badge, cx } from '../ui/primitives.tsx';
import { STAGE_PHASE_LABEL, stageKindLabel } from './StageTimeline.tsx';
import type { StagePhase } from '../../machines/contract.ts';

export function StageHeader({
  stage,
  stageIndex,
  stagePhase,
  phase,
  self,
  selfSide,
  connectionLabel,
}: {
  stage: StageDefinition | null;
  stageIndex: number;
  stagePhase: StagePhase;
  phase: MatchPhase;
  self: Participant | null;
  selfSide: Side;
  connectionLabel: string;
}) {
  const running = phase === 'RUNNING';
  const accent = selfSide === 'AFF' ? '#7DD3FC' : '#FDA4AF';
  return (
    <header
      data-testid="hud-stage-header"
      className="lens spotlight-card relative overflow-hidden px-5 py-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-[14px] bg-white/[0.025] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.14),inset_0_0_0_1px_rgba(255,255,255,0.04)]">
            <span
              className="mono text-[22px] font-semibold leading-none tracking-tight text-metal"
              aria-hidden="true"
            >
              {stage ? String(stage.index).padStart(2, '0') : '--'}
            </span>
            <span
              className="absolute right-2 top-2 h-1 w-1 rounded-full"
              style={{ background: accent, boxShadow: `0 0 8px ${accent}` }}
            />
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1
                data-testid="hud-stage-name"
                className="truncate text-[20px] font-semibold tracking-tight text-metal"
              >
                {stage?.name ?? '等待教师开赛'}
                {stage?.kind === 'CROSS_EXAM' && (
                  <span className="sr-only">{`${stage.speakerRole}质询`}</span>
                )}
              </h1>
              {stage && <Badge tone="blind">{stageKindLabel(stage.kind)}</Badge>}
              {stage && <Badge tone="neutral">{stage.speakerRole}</Badge>}
              <Badge
                tone={
                  stagePhase === 'PREP'
                    ? 'blind'
                    : stagePhase === 'ENDED'
                      ? 'neutral'
                      : running
                        ? 'pass'
                        : 'warning'
                }
              >
                {STAGE_PHASE_LABEL[stagePhase]}
              </Badge>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-500">
              <span className="mono tracking-wide">
                STAGE {stageIndex > 0 ? `${String(stageIndex).padStart(2, '0')} / 16` : '— / 16'}
              </span>
              <span className="flex items-center gap-1.5">
                <span className={cx('status-dot', running ? 'status-dot--live' : '')} />
                {connectionLabel}
              </span>
              <span>
                {self?.name ?? '未加入'} · {sideLabel(selfSide)}
                {self?.seat ? ` · ${self.seat} 辩` : ''}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="micro-label hidden xl:block">
            SPACE 切换 · TAB 锦囊 · 1/2/3 论据 · ⌘K 检索
          </span>
          {stage && (
            <span className="mono rounded-md bg-white/[0.03] px-2 py-1 text-[10px] text-slate-400 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]">
              {stage.clockMode === 'CHESS' ? 'CHESS CLOCK' : 'SINGLE TIMER'} ·{' '}
              {stage.budgets.AFF + stage.budgets.NEG}s
            </span>
          )}
        </div>
      </div>
    </header>
  );
}
